// KanbanFilters.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  HStack,
  InputGroup,
  InputLeftElement,
  Icon,
  Input,
  Select,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Text,
  Flex,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  PopoverArrow,
  PopoverCloseButton,
  ButtonGroup,
  RangeSlider,
  RangeSliderTrack,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  Badge,
  Tooltip
} from '@chakra-ui/react';
import { SearchIcon, AddIcon, PhoneIcon, ChevronDownIcon } from '@chakra-ui/icons';
import KanbanDateFilter from './KanbanDateFilter';
import { useSocket } from '../../contexts/SocketContext';

const KanbanFilters = ({
  searchQuery, setSearchQuery,
  callFilter, setCallFilter,
  tagFilter, setTagFilter,
  assigneeFilter, setAssigneeFilter,
  missedFollowUpFilter, setMissedFollowUpFilter,
  appliedDateFilter, setAppliedDateFilter,
  dateFilter, setDateFilter,
  datePreset, setDatePreset,
  focusedInput, setFocusedInput,
  users,
  handleAddNewLead,
  onCallRangeChange
}) => {
  const { socket } = useSocket();

  const [callRange, setCallRange] = useState(() => {
    // Initialize based on current callFilter
    if (callFilter === 'custom_range') {
      const urlParams = new URLSearchParams(window.location.search);
      const min = parseInt(urlParams.get('custom_range_min') || '0', 10);
      const max = parseInt(urlParams.get('custom_range_max') || '20', 10);
      return [min, max];
    }
    // Default range
    return [0, 20];
  });
  
  const [isCallRangeOpen, setIsCallRangeOpen] = useState(false);
  
  // Function to get display name for call filter
  const getCallFilterDisplay = () => {
    switch(callFilter) {
      case 'no_calls': return 'No Calls';
      case 'one_call': return '1 Call';
      case 'two_calls': return '2 Calls';
      case 'less_than_3': return '≤ 2 Calls';
      case 'less_than_5': return '≤ 4 Calls';
      case 'more_than_5': return '≥ 5 Calls';
      case 'custom_range': 
        if (callRange[0] === callRange[1]) {
          return `${callRange[0]} Call${callRange[0] !== 1 ? 's' : ''}`;
        }
        return `${callRange[0]} - ${callRange[1]} Calls`;
      default: return 'All Calls';
    }
  };
  
  // Update call range and notify parent component
  const handleCallRangeChange = (newRange) => {
    setCallRange(newRange);
    if (onCallRangeChange) {
      onCallRangeChange(newRange[0], newRange[1]);
    }
  };

  // Handle applying a custom call range
  const handleFilterByCallRange = () => {
    const urlParams = new URLSearchParams(window.location.search);
    
    if (callRange[0] === 0 && callRange[1] === 0) {
      setCallFilter('no_calls');
      urlParams.delete('custom_range_min');
      urlParams.delete('custom_range_max');
    } else if (callRange[0] === 0 && callRange[1] === 1) {
      setCallFilter('one_call');
      urlParams.delete('custom_range_min');
      urlParams.delete('custom_range_max');
    } else if (callRange[0] === 0 && callRange[1] === 2) {
      setCallFilter('less_than_3');
      urlParams.delete('custom_range_min');
      urlParams.delete('custom_range_max');
    } else if (callRange[0] === 0 && callRange[1] === 4) {
      setCallFilter('less_than_5');
      urlParams.delete('custom_range_min');
      urlParams.delete('custom_range_max');
    } else if (callRange[0] === 5 && callRange[1] >= 5) {
      setCallFilter('more_than_5');
      urlParams.delete('custom_range_min');
      urlParams.delete('custom_range_max');
    } else {
      // Set custom range parameters in URL
      urlParams.set('custom_range_min', callRange[0].toString());
      urlParams.set('custom_range_max', callRange[1].toString());
      setCallFilter('custom_range');
      
      // Notify parent component about the range change
      if (onCallRangeChange) {
        onCallRangeChange(callRange[0], callRange[1]);
      }
    }
    
    // Update URL with the new parameters without refreshing the page
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}?${urlParams}`
    );
    
    setIsCallRangeOpen(false);
  };
  
  // Listen for socket events that might affect our filters
  useEffect(() => {
    if (socket) {
      // When a call is added, we might need to update our UI if we're filtering by calls
      const handleCallAdded = (data) => {
        if (callFilter) {
          console.log('[KanbanFilters] Detected call:added event that might affect filters:', {
            leadId: data.leadId,
            callCount: data.callCount,
            currentFilter: callFilter
          });
          // If we're using a call filter, we might want to refresh or update the UI
          // The actual filtering logic is handled in KanbanBoardLogic
        }
      };

      // When a lead is updated, we should check if call count changed
      const handleLeadUpdated = (updatedLead) => {
        if (callFilter && updatedLead.callCount !== undefined) {
          console.log('[KanbanFilters] Detected lead update with call count change:', {
            leadId: updatedLead.leadId,
            callCount: updatedLead.callCount,
            currentFilter: callFilter
          });
          // The actual filtering is handled in KanbanBoardLogic
        }
      };

      // Register event listeners
      socket.on('call:added', handleCallAdded);
      socket.on('lead:updated', handleLeadUpdated);

      // Cleanup function
      return () => {
        socket.off('call:added', handleCallAdded);
        socket.off('lead:updated', handleLeadUpdated);
      };
    }
  }, [socket, callFilter]);

  return (
    <Box mb={1} px={6} pt={4}>
        <HStack spacing={1} mb={1} justify="space-between">
          <Box flex={1}>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={SearchIcon} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search leads by name, phone, or email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="sm"
              />
            </InputGroup>
          </Box>
          <HStack spacing={2}>
            {/* Call Filter Popover */}
            <Popover
              isOpen={isCallRangeOpen}
              onOpen={() => setIsCallRangeOpen(true)}
              onClose={() => setIsCallRangeOpen(false)}
              placement="bottom-start"
              closeOnBlur={false}
            >
              <PopoverTrigger>
                <Button 
                  size="sm" 
                  rightIcon={<ChevronDownIcon />} 
                  leftIcon={<PhoneIcon />}
                  variant="outline"
                  width="180px"
                >
                  {getCallFilterDisplay()}
                </Button>
              </PopoverTrigger>
              <PopoverContent width="250px">
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverHeader fontWeight="semibold">Filter by Call Count</PopoverHeader>
                <PopoverBody>
                  <Flex direction="column" gap={4}>
                    <Box>
                      <Text fontSize="sm" mb={2}>Quick Filters:</Text>
                      <ButtonGroup size="sm" isAttached variant="outline" width="100%">
                        <Button 
                          flex={1} 
                          onClick={() => { setCallFilter('all'); setIsCallRangeOpen(false); }}
                          colorScheme={callFilter === 'all' ? "blue" : "gray"}
                        >
                          All
                        </Button>
                        <Button 
                          flex={1} 
                          onClick={() => { setCallFilter('no_calls'); setIsCallRangeOpen(false); }}
                          colorScheme={callFilter === 'no_calls' ? "blue" : "gray"}
                        >
                          None
                        </Button>
                        <Button 
                          flex={1} 
                          onClick={() => { setCallFilter('one_call'); setIsCallRangeOpen(false); }}
                          colorScheme={callFilter === 'one_call' ? "blue" : "gray"}
                        >
                          1
                        </Button>
                        <Button 
                          flex={1} 
                          onClick={() => { 
                            setCallFilter('two_calls'); 
                            setIsCallRangeOpen(false); 
                          }}
                          colorScheme={callFilter === 'two_calls' ? "blue" : "gray"}
                        >
                          2
                        </Button>
                      </ButtonGroup>
                    </Box>
                    
                    <Box>
                      <Text fontSize="sm" mb={2}>Custom Range:</Text>
                      <Flex justify="space-between" mb={1}>
                        <Text fontSize="xs">{callRange[0]} call{callRange[0] !== 1 ? 's' : ''}</Text>
                        <Text fontSize="xs">{callRange[1]} call{callRange[1] !== 1 ? 's' : ''}</Text>
                      </Flex>
                      <RangeSlider
                        min={0}
                        max={20}
                        step={1}
                        value={callRange}
                        onChange={(val) => handleCallRangeChange(val)}
                        mb={3}
                      >
                        <RangeSliderTrack>
                          <RangeSliderFilledTrack />
                        </RangeSliderTrack>
                        <Tooltip label={`Min: ${callRange[0]}`} placement="top">
                          <RangeSliderThumb index={0} />
                        </Tooltip>
                        <Tooltip label={`Max: ${callRange[1]}`} placement="top">
                          <RangeSliderThumb index={1} />
                        </Tooltip>
                      </RangeSlider>
                      
                      <Flex justify="space-between">
                        <NumberInput 
                          size="xs" 
                          min={0} 
                          max={callRange[1]} 
                          value={callRange[0]}
                          onChange={(val) => handleCallRangeChange([parseInt(val), callRange[1]])}
                          maxW="60px"
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                        
                        <Text fontSize="sm" alignSelf="center">to</Text>
                        
                        <NumberInput 
                          size="xs" 
                          min={callRange[0]} 
                          max={20} 
                          value={callRange[1]}
                          onChange={(val) => handleCallRangeChange([callRange[0], parseInt(val)])}
                          maxW="60px"
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </Flex>
                    </Box>
                  </Flex>
                </PopoverBody>
                <PopoverFooter display="flex" justifyContent="flex-end">
                  <Button size="sm" colorScheme="blue" onClick={handleFilterByCallRange}>
                    Apply Range
                  </Button>
                </PopoverFooter>
              </PopoverContent>
            </Popover>

            <Select
              width="150px"
              size="sm"
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
            >
              <option value="all">All Assignees</option>
              <option value="unassigned">Unassigned</option>
              {users.map(user => (
                <option key={user.uid} value={user.uid}>
                  {user.displayName || user.email}
                </option>
              ))}
            </Select>

            <Select
              width="150px"
              size="sm"
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
            >
              <option value="all">All Tags</option>
              <option value="no_tags">No Tags</option>
              {['Hot', 'Not Responding', 'Fake Lead', 'Inquiring', 'Low Budget', 'Venues Pending', 'Proposal Pending', 'Discuss'].map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </Select>

            <Select
              width="180px"
              size="sm"
              value={missedFollowUpFilter}
              onChange={(e) => setMissedFollowUpFilter(e.target.value)}
            >
              <option value="all">All Follow-ups</option>
              <option value="missed">Missed Follow-ups Only</option>
              <option value="today">Today's Follow-ups</option>
            </Select>

            <KanbanDateFilter
                appliedDateFilter={appliedDateFilter}
                setAppliedDateFilter={setAppliedDateFilter}
                dateFilter={dateFilter}
                setDateFilter={setDateFilter}
                datePreset={datePreset}
                setDatePreset={setDatePreset}
                focusedInput={focusedInput}
                setFocusedInput={setFocusedInput}
            />
          </HStack>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={handleAddNewLead}
            size="sm"
          >
            Add Lead
          </Button>
        </HStack>
      </Box>
  );
};

export default KanbanFilters;